import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  fetchBusinessKeywordsCategories,
  deleteBusinessKeywordsCategory,
  bulkDeleteBusinessKeywordsCategories,
  bulkUpdateBusinessKeywordsCategoriesStatus,
  clearError,
  setSorting
} from './businessKeywordsCategoriesSlice';
import Loader from '../../components/Loader';
import ReactTable from '../../components/ReactTable';
import Swal from 'sweetalert2';

const BusinessKeywordsCategoriesList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, loading, error, pagination, sortBy, sortOrder } = useSelector(state => state.businessKeywordsCategories);
  
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchBusinessKeywordsCategories({
      page: currentPage,
      search: searchTerm,
      status: statusFilter,
      sortBy,
      sortOrder
    }));
  }, [dispatch, currentPage, searchTerm, statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    if (error) {
      Swal.fire({
        title: 'Error!',
        text: error,
        icon: 'error'
      });
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Debounce search input before firing queries
  useEffect(() => {
    const id = setTimeout(() => {
      const next = (searchInput || '').trim();
      setSearchTerm(next);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(id);
  }, [searchInput]);

  const handleSearch = (value) => {
    setSearchInput(value);
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSort = (columnKey, newSortOrder) => {
    dispatch(setSorting({ sortBy: columnKey, sortOrder: newSortOrder }));
    setCurrentPage(1);
  };

  const handleEdit = (id) => {
    navigate(`/business-keywords-categories/edit/${id}`);
  };

  // After mutations, refetch current page; if empty and not first page, go back a page
  const refreshAfterMutation = async () => {
    try {
      const res = await dispatch(fetchBusinessKeywordsCategories({
        page: currentPage,
        search: searchTerm,
        status: statusFilter,
        sortBy,
        sortOrder
      })).unwrap();
      const list = res?.businessKeywordsCategories || [];
      if (list.length === 0 && currentPage > 1) {
        const prevPage = Math.max(1, currentPage - 1);
        setCurrentPage(prevPage);
        await dispatch(fetchBusinessKeywordsCategories({
          page: prevPage,
          search: searchTerm,
          status: statusFilter,
          sortBy,
          sortOrder
        })).unwrap();
      }
    } catch (e) {
      // no-op; errors are handled globally via slice/error state
    }
  };

  const handleDelete = async (id) => {
    const businessKeywordsCategory = items.find(item => item._id === id);
    const businessName = businessKeywordsCategory?.businessId?.title || 'Unknown Business';
    const categoryName = Array.isArray(businessKeywordsCategory?.keywordCategoryIds)
      ? (businessKeywordsCategory.keywordCategoryIds.map(k => k?.name || '').filter(Boolean).join(', ') || 'Unknown Category')
      : 'Unknown Category';

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete the relationship between "${businessName}" and "${categoryName}". This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await dispatch(deleteBusinessKeywordsCategory(id)).unwrap();
        Swal.fire({
          title: 'Deleted!',
          text: 'Business keyword category relationship has been deleted successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
        await refreshAfterMutation();
      } catch (error) {
        Swal.fire({
          title: 'Error!',
          text: 'Failed to delete business keyword category relationship.',
          icon: 'error'
        });
      }
    }
  };

  const handleCreate = () => {
    navigate('/business-keywords-categories/create');
  };

  const headers = [
    { label: 'Business', key: 'business' },
    { label: 'Keyword Categories', key: 'keywordCategories' },
    { label: 'Status', key: 'status' },
    { label: 'Created At', key: 'createdAt' },
    { label: 'Updated At', key: 'updatedAt' }
  ];

  const actions = [
    {
      label: 'Edit',
      color: 'blue',
      onClick: handleEdit
    },
    {
      label: 'Delete',
      color: 'red',
      onClick: handleDelete
    }
  ];

  const statusLabel = (s) => {
    if (s === 1) return 'Active';
    if (s === 2) return 'Suspended';
    return 'Inactive';
  };

  const formatTableData = (rows) => rows.map((r) => ({
    _id: r._id,
    business: r.businessId?.title || 'Unknown Business',
    keywordCategories: Array.isArray(r.keywordCategoryIds) && r.keywordCategoryIds.length > 0
      ? r.keywordCategoryIds.map(k => k?.name || '').filter(Boolean).join(', ')
      : 'None',
    status: statusLabel(typeof r.status === 'number' ? r.status : Number(r.status)),
    createdAt: r.createdAt ? new Date(r.createdAt).toLocaleString() : '-',
    updatedAt: r.updatedAt ? new Date(r.updatedAt).toLocaleString() : '-'
  }));

  // Transform pagination data to match ReactTable expected format
  const transformedPagination = pagination ? {
    page: pagination.currentPage || 1,
    pages: pagination.totalPages || 1,
    total: pagination.totalItems || 0,
    limit: pagination.itemsPerPage || 10
  } : null;

  if (loading && items.length === 0) {
    return <Loader size="lg" text="Loading business keyword categories..." />;
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Business Keyword Categories</h1>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Link Businesses to Category
        </button>
      </div>

      

      {/* Table - always render so search stays visible */}
      <ReactTable
        headers={headers}
        data={formatTableData(items)}
        actions={actions}
        enableRowSelection={true}
        bulkActions={[
          {
            label: 'Activate Selected',
            color: 'green',
            onClick: async (ids, clearSelection) => {
              const result = await Swal.fire({
                title: `Activate ${ids.length} selected?`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, activate',
                cancelButtonText: 'Cancel'
              });
              if (!result.isConfirmed) return;

              try {
                await dispatch(bulkUpdateBusinessKeywordsCategoriesStatus({ ids, status: 1 })).unwrap();
                Swal.fire({ title: 'Updated!', text: 'Selected relationships activated.', icon: 'success', timer: 1500, showConfirmButton: false });
                clearSelection();
              } catch (e) {
                Swal.fire({ title: 'Error!', text: 'Failed to activate selected relationships.', icon: 'error' });
              }
            }
          },
          {
            label: 'Deactivate Selected',
            color: 'gray',
            onClick: async (ids, clearSelection) => {
              const result = await Swal.fire({
                title: `Deactivate ${ids.length} selected?`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, deactivate',
                cancelButtonText: 'Cancel'
              });
              if (!result.isConfirmed) return;

              try {
                await dispatch(bulkUpdateBusinessKeywordsCategoriesStatus({ ids, status: 0 })).unwrap();
                Swal.fire({ title: 'Updated!', text: 'Selected relationships deactivated.', icon: 'success', timer: 1500, showConfirmButton: false });
                clearSelection();
              } catch (e) {
                Swal.fire({ title: 'Error!', text: 'Failed to deactivate selected relationships.', icon: 'error' });
              }
            }
          },
          {
            label: 'Delete Selected',
            color: 'red',
            onClick: async (ids, clearSelection) => {
              const result = await Swal.fire({
                title: `Delete ${ids.length} selected?`,
                text: 'This action cannot be undone!',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, delete',
                cancelButtonText: 'Cancel'
              });
              if (!result.isConfirmed) return;

              try {
                await dispatch(bulkDeleteBusinessKeywordsCategories(ids)).unwrap();
                Swal.fire({ title: 'Deleted!', text: 'Selected relationships deleted.', icon: 'success', timer: 1500, showConfirmButton: false });
                clearSelection();
                await refreshAfterMutation();
              } catch (e) {
                Swal.fire({ title: 'Error!', text: 'Failed to delete selected relationships.', icon: 'error' });
              }
            }
          }
        ]}
        pagination={transformedPagination}
        onPageChange={handlePageChange}
        loading={loading}
        sortBy={sortBy}
        searchKey="keywordCategories"
        searchValue={searchTerm}
        onSearch={handleSearch}
        searchDelay={1000}
        sortOrder={sortOrder}
        onSort={handleSort}
      />

      {/* Empty state shown under the table when there are no rows */}
      {items.length === 0 && (
        <div className="text-center py-8">
          <div className="text-gray-500 text-lg mb-4">No business keyword category relationships found</div>
          <p className="text-gray-400">
            {(searchTerm || statusFilter)
              ? 'Try adjusting your search criteria or filters.'
              : 'Link businesses to keyword categories to get started.'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default BusinessKeywordsCategoriesList;
